/**
 * useCanvas — 4-tab clinical canvas with API-backed persistence.
 *
 * Tabs: HPI, PE, Assessment & Plan, Notes.
 * Each tab tracks content, dirty state, and version history.
 * Canvas updates arrive via SSE `artifact_update` events (setTabContentFromEvent).
 * Supports word-level diff suggestions and artifact API persistence.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { diffWords, type Change } from 'diff'
import api from '../services/api'
import { generateId } from '../utils/idUtils'
import { TAB_LABELS, normalizeTabKey } from '../utils/canvasUtils'
import { stripHtml } from '../utils/domUtils'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type CanvasTab = 'hpi' | 'pe' | 'assessment_and_plan' | 'notes'

export interface CanvasTabState {
  content: string
  html: string
  isDirty: boolean
  lastSavedAt: string | null
}

export interface CanvasVersion {
  id: string
  content: string
  html: string
  timestamp: string
  source: 'user' | 'agent' | 'auto_save' | 'pre-overwrite' | 'pre-suggestion'
}

export interface SuggestionHunk {
  id: string
  contextBefore: string
  removed: string
  added: string
  contextAfter: string
  status: 'pending' | 'accepted' | 'rejected'
  diffStartIdx: number
  diffEndIdx: number
  textFrom: number
  textTo: number
  type: 'removed' | 'added' | 'replaced'
  addedText: string | null
}

export interface CanvasSuggestion {
  tab: CanvasTab
  original: string
  proposed: string
  diffs: Change[]
  hunks: SuggestionHunk[]
  resolvedCount: number
  totalCount: number
}

interface Artifact {
  id: string
  type: string
  title: string
  content: string
  current_content?: string
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const MAX_VERSIONS = 20
const CONTEXT_WORDS = 8
// TAB_LABELS and normalizeTabKey imported from ../utils/canvasUtils

/** Canonical internal tab-title map (used by saveToArtifact for artifact titles).
 *  Re-exported alias of TAB_LABELS from '../utils/canvasUtils'. */
export const TAB_TITLES = TAB_LABELS as Record<CanvasTab, string>

// Configure marked: powers snapshot HTML (getCanvasSnapshots/stripHtml) and the
// word-level diff suggestions — NOT the CanvasEditor view, which now renders
// markdown via DocumentBody/ReactMarkdown.
marked.use({ breaks: true, gfm: true })

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

const emptyTabs = (): Record<CanvasTab, CanvasTabState> => ({
  hpi: { content: '', html: '', isDirty: false, lastSavedAt: null },
  pe: { content: '', html: '', isDirty: false, lastSavedAt: null },
  assessment_and_plan: { content: '', html: '', isDirty: false, lastSavedAt: null },
  notes: { content: '', html: '', isDirty: false, lastSavedAt: null },
})

const emptyVersions = (): Record<CanvasTab, CanvasVersion[]> => ({
  hpi: [],
  pe: [],
  assessment_and_plan: [],
  notes: [],
})

// stripHtml imported from ../utils/domUtils (was stripHtmlToText)

// ────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────

interface UseCanvasOptions {
  workspaceId?: string | null
}

export function useCanvas({ workspaceId = null }: UseCanvasOptions = {}) {
  const [tabs, setTabs] = useState<Record<CanvasTab, CanvasTabState>>(emptyTabs)
  const [activeTab, setActiveTab] = useState<CanvasTab>('hpi')
  const [versions, setVersions] = useState<Record<CanvasTab, CanvasVersion[]>>(emptyVersions)
  const [suggestion, setSuggestion] = useState<CanvasSuggestion | null>(null)

  // Ref for stable access to current tab content in closures
  const tabsRef = useRef<Record<CanvasTab, CanvasTabState>>(emptyTabs())
  tabsRef.current = tabs

  // Track artifact IDs per tab for update vs. create
  const artifactIds = useRef<Record<CanvasTab, string | null>>({
    hpi: null,
    pe: null,
    assessment_and_plan: null,
    notes: null,
  })

  // ── Tab content operations ──

  const setTabContent = useCallback((
    tab: CanvasTab,
    content: string,
    source: CanvasVersion['source'] = 'user'
  ) => {
    const html = typeof content === 'string' ? DOMPurify.sanitize(marked.parse(content, { async: false }) as string) : ''

    // Save previous content as a version before overwriting
    const prev = tabsRef.current[tab]
    if (prev.content && prev.content.length > 30) {
      setVersions(prevVersions => {
        const entry: CanvasVersion = {
          id: generateId('cv'),
          content: prev.content,
          html: prev.html,
          timestamp: new Date().toISOString(),
          source: 'pre-overwrite',
        }
        const tabVersions = [...(prevVersions[tab] || []), entry].slice(-MAX_VERSIONS)
        return { ...prevVersions, [tab]: tabVersions }
      })
    }

    setTabs(prev => ({
      ...prev,
      [tab]: {
        content,
        html,
        isDirty: source === 'user',
        lastSavedAt: source !== 'user' ? new Date().toISOString() : prev[tab].lastSavedAt,
      },
    }))
  }, [])

  const setTabContentFromEvent = useCallback((eventTab: string, content: string) => {
    if (eventTab === 'hpi_pe') {
      // HPI+PE comes as a single blob — split on PE header variants
      // Supports: "Physical Exam:", "PE:", "**Physical Exam:**", "## Physical Exam", etc.
      // Also handle PE header at start of content (PE-only payload, splitIdx === 0)
      const PE_SPLIT = /(?:^|\n)\s*(?:#{1,3}\s+)?(?:\*{0,2}\s*)?(?:Physical Exam|PE)\s*:?\s*\*{0,2}\s*\n/im
      const splitIdx = content.search(PE_SPLIT)

      if (splitIdx >= 0) {
        const hpiRaw = splitIdx > 0 ? content.slice(0, splitIdx).trim() : ''
        const peRaw = content.slice(splitIdx).replace(PE_SPLIT, '').trim()

        if (hpiRaw) {
          // Strip leading HPI header if present (plain, bold, or markdown heading)
          const hpiContent = hpiRaw.replace(/^\s*(?:#{1,3}\s+)?(?:\*{0,2}\s*)?HPI\s*:?\s*\*{0,2}\s*\n?/i, '').trim()
          setTabContent('hpi', hpiContent, 'agent')
        }
        if (peRaw) {
          setTabContent('pe', peRaw, 'agent')
        }
      } else {
        // No PE header found — put everything in HPI
        setTabContent('hpi', content, 'agent')
      }
    } else {
      const tab = normalizeTabKey(eventTab)
      if (tab) {
        setTabContent(tab, content, 'agent')
      }
    }
  }, [setTabContent])

  const getTabContent = useCallback((tab: CanvasTab): string => {
    return tabsRef.current[tab].content
  }, [])

  const updateTabContent = useCallback((tab: CanvasTab, content: string) => {
    // Fix #21: Block empty updates from TipTap initialization overwriting hydrated content.
    // When loadFromArtifacts sets content (isDirty=false), TipTap's onUpdate fires with ''
    // asynchronously — the empty setTabs call processes AFTER the loaded content, wiping it.
    // Guard: only allow empty updates when the user has already started editing (isDirty=true).
    if (!content && !tabsRef.current[tab].isDirty) return

    const html = DOMPurify.sanitize(marked.parse(content, { async: false }) as string)
    setTabs(prev => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        content,
        html,
        isDirty: true,
      },
    }))
  }, [])

  // ── Snapshot for API requests ──

  const getCanvasSnapshots = useCallback((): Record<CanvasTab, string> => {
    const current = tabsRef.current
    return {
      hpi: stripHtml(current.hpi.html),
      pe: stripHtml(current.pe.html),
      assessment_and_plan: stripHtml(current.assessment_and_plan.html),
      notes: stripHtml(current.notes.html),
    }
  }, [])

  // ── Persistence via artifact API ──

  const saveToArtifact = useCallback(async (tab: CanvasTab) => {
    if (!workspaceId) return

    const content = tabsRef.current[tab].content
    if (!content) return

    try {
      const existingId = artifactIds.current[tab]

      if (existingId) {
        await api.post(`/api/v1/artifacts/${existingId}/versions`, {
          content,
          is_manual_save: true,
          edit_description: `Manual save of ${TAB_TITLES[tab]}`,
        })
      } else {
        const response = await api.post('/api/v1/artifacts', {
          case_id: workspaceId,
          type: tab,
          title: TAB_TITLES[tab],
          content,
        })
        artifactIds.current[tab] = response.data.artifact_id
      }

      setTabs(prev => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          isDirty: false,
          lastSavedAt: new Date().toISOString(),
        },
      }))
    } catch (error) {
      console.error(`Failed to save ${tab} artifact:`, error)
      throw error
    }
  }, [workspaceId])

  const loadFromArtifacts = useCallback((artifacts: Artifact[]) => {
    for (const artifact of artifacts) {
      const tab = normalizeTabKey(artifact.type)
      if (!tab) continue

      const content = artifact.current_content || artifact.content
      if (!content) continue

      const html = DOMPurify.sanitize(marked.parse(content, { async: false }) as string)
      artifactIds.current[tab] = artifact.id

      setTabs(prev => ({
        ...prev,
        [tab]: {
          content,
          html,
          isDirty: false,
          lastSavedAt: new Date().toISOString(),
        },
      }))
    }
  }, [])

  // ── Suggestion system (word-level diff) ──

  const buildHunks = useCallback((diffs: Change[]): SuggestionHunk[] => {
    const hunks: SuggestionHunk[] = []

    // Pre-compute character offsets in the original text
    const origOffsets: number[] = []
    let running = 0
    for (const part of diffs) {
      origOffsets.push(running)
      if (!part.added) running += part.value.length
    }

    let i = 0
    while (i < diffs.length) {
      if (!diffs[i].added && !diffs[i].removed) { i++; continue }

      const changeStart = i
      const removedParts: string[] = []
      const addedParts: string[] = []

      while (i < diffs.length && (diffs[i].added || diffs[i].removed)) {
        if (diffs[i].removed) removedParts.push(diffs[i].value)
        if (diffs[i].added) addedParts.push(diffs[i].value)
        i++
      }

      let contextBefore = ''
      if (changeStart > 0 && !diffs[changeStart - 1].added && !diffs[changeStart - 1].removed) {
        const words = diffs[changeStart - 1].value.split(/(\s+)/)
        contextBefore = words.slice(-CONTEXT_WORDS * 2).join('')
      }

      let contextAfter = ''
      if (i < diffs.length && !diffs[i].added && !diffs[i].removed) {
        const words = diffs[i].value.split(/(\s+)/)
        contextAfter = words.slice(0, CONTEXT_WORDS * 2).join('')
      }

      const removedText = removedParts.join('')
      const addedText = addedParts.join('')
      const textFrom = origOffsets[changeStart]
      const textTo = textFrom + removedText.length

      let type: SuggestionHunk['type']
      if (removedText && addedText) type = 'replaced'
      else if (removedText) type = 'removed'
      else type = 'added'

      hunks.push({
        id: generateId('hunk'),
        contextBefore,
        removed: removedText,
        added: addedText,
        contextAfter,
        status: 'pending',
        diffStartIdx: changeStart,
        diffEndIdx: i,
        textFrom,
        textTo,
        type,
        addedText: addedText || null,
      })
    }

    return hunks
  }, [])

  const proposeSuggestion = useCallback((tab: CanvasTab, agentMarkdown: string) => {
    const currentHtml = tabsRef.current[tab].html || ''
    const currentText = stripHtml(currentHtml)
    const proposedHtml = marked.parse(agentMarkdown) as string
    const proposedText = stripHtml(proposedHtml)

    const diffs = diffWords(currentText, proposedText)
    const hunks = buildHunks(diffs)

    if (hunks.length === 0) return

    setSuggestion({
      tab,
      original: currentHtml,
      proposed: proposedHtml,
      diffs,
      hunks,
      resolvedCount: 0,
      totalCount: hunks.length,
    })
  }, [buildHunks])

  const reconstructFromHunks = useCallback((diffs: Change[], hunks: SuggestionHunk[]): string => {
    let result = ''
    const hunkMap = new Map<number, SuggestionHunk['status']>()

    for (const hunk of hunks) {
      for (let j = hunk.diffStartIdx; j < hunk.diffEndIdx; j++) {
        hunkMap.set(j, hunk.status)
      }
    }

    for (let i = 0; i < diffs.length; i++) {
      const part = diffs[i]
      if (!part.added && !part.removed) {
        result += part.value
      } else if (hunkMap.has(i)) {
        const status = hunkMap.get(i)
        if (status === 'accepted') {
          if (part.added) result += part.value
        } else {
          if (part.removed) result += part.value
        }
      }
    }
    return result
  }, [])

  const acceptHunk = useCallback((index: number) => {
    setSuggestion(prev => {
      if (!prev) return prev
      const newHunks = prev.hunks.map((h, i) =>
        i === index ? { ...h, status: 'accepted' as const } : h
      )
      const resolvedCount = newHunks.filter(h => h.status !== 'pending').length
      return { ...prev, hunks: newHunks, resolvedCount }
    })
  }, [])

  const rejectHunk = useCallback((index: number) => {
    setSuggestion(prev => {
      if (!prev) return prev
      const newHunks = prev.hunks.map((h, i) =>
        i === index ? { ...h, status: 'rejected' as const } : h
      )
      const resolvedCount = newHunks.filter(h => h.status !== 'pending').length
      return { ...prev, hunks: newHunks, resolvedCount }
    })
  }, [])

  const acceptAll = useCallback(() => {
    setSuggestion(prev => {
      if (!prev) return prev
      const newHunks = prev.hunks.map(h =>
        h.status === 'pending' ? { ...h, status: 'accepted' as const } : h
      )
      return { ...prev, hunks: newHunks, resolvedCount: newHunks.length }
    })
  }, [])

  const rejectAll = useCallback(() => {
    setSuggestion(prev => {
      if (!prev) return prev
      const newHunks = prev.hunks.map(h =>
        h.status === 'pending' ? { ...h, status: 'rejected' as const } : h
      )
      return { ...prev, hunks: newHunks, resolvedCount: newHunks.length }
    })
  }, [])

  const finalizeSuggestion = useCallback(() => {
    if (!suggestion) return

    const { tab } = suggestion

    // Save current content to history before applying
    const prev = tabsRef.current[tab]
    if (prev.content && prev.content.length > 30) {
      setVersions(prevVersions => {
        const entry: CanvasVersion = {
          id: generateId('cv'),
          content: prev.content,
          html: prev.html,
          timestamp: new Date().toISOString(),
          source: 'pre-suggestion',
        }
        const tabVersions = [...(prevVersions[tab] || []), entry].slice(-MAX_VERSIONS)
        return { ...prevVersions, [tab]: tabVersions }
      })
    }

    const finalText = reconstructFromHunks(suggestion.diffs, suggestion.hunks)
    const finalHtml = marked.parse(finalText) as string

    setTabs(prev => ({
      ...prev,
      [tab]: {
        content: finalText,
        html: finalHtml,
        isDirty: true,
        lastSavedAt: prev[tab].lastSavedAt,
      },
    }))

    setSuggestion(null)
  }, [suggestion, reconstructFromHunks])

  // Auto-finalize when all hunks are resolved
  useEffect(() => {
    if (suggestion && suggestion.hunks.length > 0 && suggestion.resolvedCount === suggestion.totalCount) {
      finalizeSuggestion()
    }
  }, [suggestion, finalizeSuggestion])

  // ── Version navigation ──

  const restoreVersion = useCallback((tab: CanvasTab, versionId: string) => {
    const tabVersions = versions[tab] || []
    const version = tabVersions.find(v => v.id === versionId)
    if (!version) return

    setTabs(prev => ({
      ...prev,
      [tab]: {
        content: version.content,
        html: version.html,
        isDirty: true,
        lastSavedAt: prev[tab].lastSavedAt,
      },
    }))
  }, [versions])

  // ── Clear all ──

  const clearCanvases = useCallback(() => {
    setTabs(emptyTabs())
    setVersions(emptyVersions())
    setSuggestion(null)
    artifactIds.current = {
      hpi: null,
      pe: null,
      assessment_and_plan: null,
      notes: null,
    }
  }, [])

  return {
    // State
    tabs,
    activeTab,
    versions,
    suggestion,

    // Tab operations
    setActiveTab,
    setTabContent,
    setTabContentFromEvent,
    getTabContent,
    updateTabContent,
    getCanvasSnapshots,

    // Suggestions
    proposeSuggestion,
    acceptHunk,
    rejectHunk,
    acceptAll,
    rejectAll,
    finalizeSuggestion,

    // Persistence
    saveToArtifact,
    loadFromArtifacts,

    // Version history
    restoreVersion,

    // Reset
    clearCanvases,
  }
}

export type UseCanvasReturn = ReturnType<typeof useCanvas>
